import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdImage } from 'react-icons/md';
import AnnotatorCanvas from './components/AnnotatorCanvas';
import AnnotatorToolbar from './components/AnnotatorToolbar';
import { useAnnotatorHistory } from './hooks/useAnnotatorHistory';
import { copyCanvasToClipboard, downloadCanvasPng } from './utils/draw';
import { clampCropToImage, rasterizeCroppedImage, shiftElementsAfterCrop } from './utils/crop';
import { newId } from './utils/id';
import { sendEvent } from '../../common/analytics';

function loadImageFromFile(file) {
  if (!file || !file.type.startsWith('image/')) return Promise.reject(new Error('Not an image'));
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

function loadImageFromBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
}

const ImageMarker = () => {
  const [image, setImage] = useState(null);
  const [tool, setTool] = useState('select');
  const [color, setColor] = useState('#ef4444');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [quickBlur, setQuickBlur] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  const stageRef = useRef(null);
  const dropRef = useRef(null);
  const toast = useToast();

  const {
    elements,
    setElements,
    setElementsDirect,
    beginGesture,
    endGesture,
    reset,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useAnnotatorHistory([]);

  const border = useColorModeValue('gray.200', 'gray.600');
  const dropBg = useColorModeValue('gray.50', 'gray.800');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const ingestImage = useCallback(
    async (img) => {
      setImage(img);
      reset([]);
      setSelectedId(null);
      setEditingTextId(null);
      setTool('select');
    },
    [reset]
  );

  useEffect(() => {
    setSelectedId(null);
    // editingTextId is cleared by Image Marker canvas when committing inline text (tool change).
  }, [tool]);

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    setElements((els) => els.filter((e) => e.id !== selectedId));
    setSelectedId(null);
    setEditingTextId(null);
  }, [selectedId, setElements]);

  const duplicateSelected = useCallback(() => {
    if (!selectedId) return;
    const el = elements.find((e) => e.id === selectedId);
    if (!el) return;
    const copy = JSON.parse(JSON.stringify(el));
    copy.id = newId();
    const nudge = 12;
    if (copy.type === 'rect' || copy.type === 'blur') {
      copy.x += nudge;
      copy.y += nudge;
    } else if (copy.type === 'text') {
      copy.x += nudge;
      copy.y += nudge;
    } else if (copy.type === 'arrow') {
      copy.x1 += nudge;
      copy.y1 += nudge;
      copy.x2 += nudge;
      copy.y2 += nudge;
    } else if (copy.type === 'path') {
      copy.points = copy.points.map((p) => ({ x: p.x + nudge, y: p.y + nudge }));
    }
    setElements((els) => [...els, copy]);
    setSelectedId(copy.id);
    setEditingTextId(null);
  }, [selectedId, elements, setElements]);

  const bringForward = useCallback(() => {
    if (!selectedId) return;
    setElements((els) => {
      const i = els.findIndex((e) => e.id === selectedId);
      if (i < 0 || i >= els.length - 1) return els;
      const next = els.slice();
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next;
    });
  }, [selectedId, setElements]);

  const sendBackward = useCallback(() => {
    if (!selectedId) return;
    setElements((els) => {
      const i = els.findIndex((e) => e.id === selectedId);
      if (i <= 0) return els;
      const next = els.slice();
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
  }, [selectedId, setElements]);

  const handleApplyCrop = useCallback(
    async (rect, elsBefore) => {
      if (!image) return;
      const crop = clampCropToImage(image.naturalWidth, image.naturalHeight, rect);
      if (crop.w < 8 || crop.h < 8) return;
      const measureCtx = document.createElement('canvas').getContext('2d');
      try {
        const newImg = await rasterizeCroppedImage(image, crop);
        const next = shiftElementsAfterCrop(elsBefore, crop, measureCtx);
        setImage(newImg);
        reset(next);
        setSelectedId(null);
        setEditingTextId(null);
        setTool('select');
        toast({ title: 'Image cropped', status: 'success', duration: 2000 });
        sendEvent('ImageMarker', 'Crop', 'apply');
      } catch {
        toast({ title: 'Could not crop image', status: 'error', duration: 3000 });
      }
    },
    [image, reset, toast]
  );

  const onFiles = useCallback(
    async (files) => {
      const f = files?.[0];
      if (!f) return;
      try {
        const img = await loadImageFromFile(f);
        await ingestImage(img);
        sendEvent('ImageMarker', 'Load', 'file');
      } catch {
        toast({ title: 'Could not load image', status: 'error', duration: 3000 });
      }
    },
    [ingestImage, toast]
  );

  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.type.startsWith('image/')) {
          e.preventDefault();
          const blob = it.getAsFile();
          if (!blob) continue;
          loadImageFromBlob(blob)
            .then((img) => ingestImage(img))
            .then(() => sendEvent('ImageMarker', 'Load', 'paste'))
            .catch(() =>
              toast({ title: 'Could not paste image', status: 'error', duration: 3000 })
            );
          break;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [ingestImage, toast]);

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
          /* allow default in inputs for text undo */
          return;
        }
        if (!e.metaKey && !e.ctrlKey) return;
      }

      if (e.metaKey || e.ctrlKey) {
        if (e.key.toLowerCase() === 'z') {
          e.preventDefault();
          if (e.shiftKey) redo();
          else undo();
          return;
        }
        if (e.key.toLowerCase() === 'y') {
          e.preventDefault();
          redo();
          return;
        }
        if (e.key.toLowerCase() === 'd' && image) {
          e.preventDefault();
          duplicateSelected();
          return;
        }
      }

      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable))
        return;

      if (
        image &&
        selectedId &&
        (e.key === 'Delete' || e.key === 'Backspace')
      ) {
        e.preventDefault();
        deleteSelected();
        return;
      }

      const k = e.key.toLowerCase();
      if (k === 'v') setTool('select');
      if (k === 'c' && image && !e.metaKey && !e.ctrlKey) setTool('crop');
      if (k === 'r') setTool('rect');
      if (k === 'a') setTool('arrow');
      if (k === 't') setTool('text');
      if (k === 'b') setTool('blur');
      if (k === 'p') setTool('pen');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [redo, undo, image, selectedId, deleteSelected, duplicateSelected]);

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDraggingFile(true);
  };

  const onDragLeave = (e) => {
    if (!dropRef.current?.contains(e.relatedTarget)) setIsDraggingFile(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const f = e.dataTransfer.files?.[0];
    if (f) onFiles([f]);
  };

  const handleDownload = () => {
    const c = stageRef.current?.toCompositeCanvas?.();
    if (!c) return;
    downloadCanvasPng(c);
    sendEvent('ImageMarker', 'Export', 'png');
  };

  const handleCopy = async () => {
    const c = stageRef.current?.toCompositeCanvas?.();
    if (!c) return;
    try {
      await copyCanvasToClipboard(c);
      toast({ title: 'Copied to clipboard', status: 'success', duration: 2000 });
      sendEvent('ImageMarker', 'Export', 'clipboard');
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Your browser may block clipboard images, or the API is unavailable.',
        status: 'warning',
        duration: 4000,
      });
    }
  };

  return (
    <Box maxW="100%" mx="auto" pb={32}>
      <VStack align="stretch" spacing={4} mb={6}>
        <Heading size="lg" fontWeight="700" letterSpacing="-0.02em">
          Image Marker
        </Heading>
        <Text fontSize="sm" color={muted} maxW="560px">
          Paste an image (⌘V), drop a file, then mark it up with shapes, arrows, text, and pixel
          blur. Everything stays in your browser; export at full resolution.
        </Text>
      </VStack>

      <Box
        ref={dropRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={isDraggingFile ? 'blue.300' : border}
        bg={dropBg}
        minH={{ base: '320px', md: '420px' }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={{ base: 3, md: 6 }}
        transition="border-color 0.15s ease"
      >
        {!image ? (
          <VStack spacing={4} textAlign="center" py={10} px={4}>
            <Box color="gray.400">
              <MdImage size={48} />
            </Box>
            <Text fontWeight="600" color="gray.700">
              Drop an image here or paste from the clipboard
            </Text>
            <Text fontSize="sm" color={muted}>
              PNG, JPG, WebP — processed only on your device
            </Text>
          </VStack>
        ) : (
          <Flex justify="center" align="center" w="full">
            <AnnotatorCanvas
              ref={stageRef}
              image={image}
              elements={elements}
              setElements={setElements}
              setElementsDirect={setElementsDirect}
              beginGesture={beginGesture}
              endGesture={endGesture}
              tool={tool}
              color={color}
              strokeWidth={strokeWidth}
              selectedId={selectedId}
              setSelectedId={setSelectedId}
              editingTextId={editingTextId}
              setEditingTextId={setEditingTextId}
              quickBlur={quickBlur}
              setTool={setTool}
              onDeleteSelected={deleteSelected}
              onDuplicateSelected={duplicateSelected}
              onBringForward={bringForward}
              onSendBackward={sendBackward}
              onApplyCrop={handleApplyCrop}
            />
          </Flex>
        )}
      </Box>

      <AnnotatorToolbar
        tool={tool}
        onTool={setTool}
        color={color}
        onColor={setColor}
        strokeWidth={strokeWidth}
        onStrokeWidth={setStrokeWidth}
        quickBlur={quickBlur}
        onQuickBlur={setQuickBlur}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        onDownload={handleDownload}
        onCopy={handleCopy}
        hasImage={!!image}
        copyDisabledReason={null}
      />
    </Box>
  );
};

export default ImageMarker;
